"use client"

import { useState, type ReactNode } from "react"
import { ExternalLink, Loader2, Store } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { usePermissions } from "@/hooks/use-permissions"
import { INTEGRATIONS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { useStorefrontWidgetStatusQuery } from "@/hooks/queries/shopify-storefront-widget"
import { ShopifyAddChatDialog } from "./shopify-add-chat-dialog"
import { ShopifyRemoveChatDialog } from "./shopify-remove-chat-dialog"
import type { StorefrontWidgetState, StorefrontWidgetStoreView } from "@/server-actions/shopify/storefront-widget"
import { useTranslations } from "next-intl"


interface ShopifyStorefrontWidgetProps {
  channelId: string
  canManage: boolean
  /** Manual install steps, shown when no store is connected through the Versafy AI app. */
  fallback: ReactNode
}

export function ShopifyStorefrontWidget({ channelId, canManage, fallback }: ShopifyStorefrontWidgetProps) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsShopifyStorefrontWidget")

  const { can } = usePermissions()
  const status = useStorefrontWidgetStatusQuery(channelId)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  if (status.isPending) {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /><span><span key={tUi("checkingYourShopifyStores")}>{tUi("checkingYourShopifyStores")}</span></span>
      </p>
    )
  }
  if (status.isError || !status.data.length) return <>{fallback}</>

  const stores = status.data
  const store = stores.find(item => item.integrationId === selectedId) ?? stores[0]
  const canWrite = canManage && can(INTEGRATIONS_PERMISSIONS.SHOPIFY_MANAGE)

  return (
    <div className="space-y-2">
      {stores.length > 1 && (
        <Select value={store.integrationId} onValueChange={setSelectedId}>
          <SelectTrigger className="h-8 text-xs" aria-label={tUi("shopifyStore")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {stores.map(item => (
              <SelectItem key={item.integrationId} value={item.integrationId} className="text-xs">
                {item.publicDomain}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <StoreRow channelId={channelId} store={store} canWrite={canWrite} />
    </div>
  )
}

function StoreRow({ channelId, store, canWrite }: {
  channelId: string
  store: StorefrontWidgetStoreView
  canWrite: boolean
}) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsShopifyStorefrontWidget")

  // Captured on click: the row is reused when another store is picked while a dialog is open.
  const [adding, setAdding] = useState<StorefrontWidgetStoreView | null>(null)
  const [removing, setRemoving] = useState<StorefrontWidgetStoreView | null>(null)

  return (
    <div className="space-y-2 rounded-md border bg-background px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex min-w-[10rem] flex-1 items-center gap-1.5 text-xs">
          <Store className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate font-medium">{store.publicDomain}</span>
        </p>
        <span className="text-xs text-muted-foreground">{STATE_LABELS[store.state]}</span>
      </div>

      {store.state === "unavailable" && (
        <p className="text-xs text-amber-700">
          <span key={tUi("versafyCouldNotReachThisStoreReconnectIt")}>{tUi("versafyCouldNotReachThisStoreReconnectIt")}</span></p>
      )}

      {canWrite && store.state !== "unavailable" && (
        <div className="flex flex-wrap gap-2">
          {store.state === "this_widget" ? (
            <>
              <Button asChild variant="brand" size="sm" className="h-8 gap-1.5 px-3 text-xs">
                <a href={store.themeEditorUrl} target="_blank" rel="noopener noreferrer">
                  <span><span key={tUi("openThemeEditor")}>{tUi("openThemeEditor")}</span></span><ExternalLink className="h-3.5 w-3.5" />
                </a>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 px-3 text-xs"
                onClick={() => setRemoving(store)}
              >
                <span><span key={tUi("removeFromStore")}>{tUi("removeFromStore")}</span></span>
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="brand"
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={() => setAdding(store)}
            >
              <span><span key={tUi("addToShopifyStore")}>{tUi("addToShopifyStore")}</span></span>
            </Button>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">{STATE_HINTS[store.state]}</p>

      {adding && (
        <ShopifyAddChatDialog
          open
          onOpenChange={open => { if (!open) setAdding(null) }}
          channelId={channelId}
          store={adding}
        />
      )}
      {removing && (
        <ShopifyRemoveChatDialog
          open
          onOpenChange={open => { if (!open) setRemoving(null) }}
          store={removing}
        />
      )}
    </div>
  )
}


const STATE_LABELS: Record<StorefrontWidgetState, string> = {
  this_widget: "Added — confirm in the theme editor",
  other_widget: "Another widget is on this store",
  none: "Not added yet",
  unavailable: "Store unavailable",
}

const STATE_HINTS: Record<StorefrontWidgetState, string> = {
  this_widget:
    "In the theme editor, make sure Chat widget is on under App embeds, then click Save. If you pasted the Versafy code into theme.liquid earlier, remove it.",
  other_widget: "Adding replaces the chat now on the store. After regenerating this widget's token, add it again.",
  none: "Adds the chat to your storefront without editing theme code. You confirm it once in the Shopify theme editor.",
  unavailable: "The widget on your storefront is not affected.",
}
